import React from 'react';
import Navigation from '../components/Navigation';
import { supabase } from '../lib/supabase';

interface TermsAndConditionsProps {
  navigate: (to: string) => void;
}

export default function TermsAndConditions({ navigate }: TermsAndConditionsProps) {
  return (
    <div className="min-h-screen bg-white">
      <Navigation
        onSignOut={async () => await supabase.auth.signOut()}
        tasks={[]}
        currentView="all"
        onViewChange={() => {}}
        navigate={navigate}
      />

      <main className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-sm rounded-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Terms and Conditions</h1>
          <p className="text-sm text-gray-500 mb-8">Effective Date: 10/04/25</p>
          
          <div className="prose prose-indigo max-w-none">
            <p className="text-gray-700">
              Welcome to PM Pro AI. By accessing or using our application, you agree to be bound by the following Terms and Conditions. Please read them carefully before using the service. If you do not agree with any part of these terms, you should not use the application.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700">
              By creating an account or using PM Pro AI, you confirm that you:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Are at least 10 years old (or the legal age in your country).</li>
              <li>Have read, understood, and agreed to these Terms and Conditions.</li>
              <li>Are authorized to use the service if you are acting on behalf of a business or organization.</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">2. Description of Service</h2>
            <p className="text-gray-700">
              PM Pro AI is a project management platform enhanced with AI tools, including:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>AI agents for summarizing meetings, task planning, team analysis, etc.</li>
              <li>Project, task, and resource management features.</li>
              <li>Integration with third-party AI APIs (e.g., OpenAI) using your own API key.</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">3. User Responsibilities</h2>
            <p className="text-gray-700">
              You agree to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Provide accurate account and contact information.</li>
              <li>Maintain the confidentiality of your account credentials.</li>
              <li>Use PM Pro AI only for lawful purposes.</li>
              <li>Not use the platform to share illegal, offensive, or harmful content.</li>
            </ul>
            <p className="text-gray-700">
              You are fully responsible for the content, prompts, and data you input into the platform.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">4. Use of AI Features and API Keys</h2>
            <p className="text-gray-700">
              To access AI functionalities, users may choose to connect their own OpenAI (or other LLM) API keys.
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Your API key is encrypted and used only to process AI interactions.</li>
              <li>You are responsible for any usage costs or rate limits tied to your key.</li>
              <li>We are not responsible for third-party service availability, outages, or inaccuracies in AI-generated responses.</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">5. Intellectual Property</h2>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>All content, features, design, and code within PM Pro AI are the intellectual property of PM Pro AI unless otherwise stated.</li>
              <li>You retain ownership of your data and content.</li>
              <li>You may not copy, resell, or modify any part of the service without written permission.</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">6. Account Suspension or Termination</h2>
            <p className="text-gray-700">
              We reserve the right to suspend or terminate user accounts that:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Violate these Terms.</li>
              <li>Abuse the system or AI tools.</li>
              <li>Threaten the integrity or security of the platform.</li>
            </ul>
            <p className="text-gray-700">
              You may cancel your account at any time. Upon termination, your access to the platform and its features will cease.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">7. Data Usage and Privacy</h2>
            <p className="text-gray-700">
              Your data remains private and is never sold or shared. We follow a strict Privacy Policy to ensure your information is protected. While we have access to the database for maintenance and support, we do not use or process user data beyond what is required for platform functionality.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">8. Service Availability</h2>
            <p className="text-gray-700">
              We aim for 99.9% uptime but do not guarantee uninterrupted service. There may be occasional updates, bugs, or third-party issues that could impact availability. We are not liable for any losses resulting from service downtime.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">9. Limitation of Liability</h2>
            <p className="text-gray-700">
              PM Pro AI and its creators shall not be liable for:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Any indirect, incidental, or consequential damages.</li>
              <li>Loss of data, business, or profits arising from use or inability to use the platform.</li>
              <li>AI-generated content inaccuracies or outcomes.</li>
            </ul>
            <p className="text-gray-700">
              Use of the platform and AI results is at your own risk.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">10. Modifications to the Service or Terms</h2>
            <p className="text-gray-700">
              We may update the platform or these Terms at any time. When we do, we will post the new version and notify users if the changes are significant.
            </p>
            <p className="text-gray-700">
              Continuing to use the platform after changes means you accept the updated terms.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">11. Contact</h2>
            <p className="text-gray-700">
              For any questions about these Terms and Conditions, reach out to us at:
            </p>
            <p className="text-gray-700">
              Email: <a href="mailto:hello@pmproai.com" className="text-indigo-600 hover:text-indigo-500">hello@pmproai.com</a>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}